import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import moment from 'moment'
import classnames from "classnames/bind";

import {urlParamUpdated} from 'js/helpers/urlHelpers';
import Button from 'js/components/shared/Button';
import {queryParameters} from 'js/helpers/drupalSettings';
import {replace as replaceString} from 'js/helpers/string';
import {isDesktop} from 'js/helpers/browser';

import HotelListCard from '../HotelListCard';
import HotelListCardMini from '../HotelListCardMini';
import SearchList from '../SearchList/SearchList'
import GoogleMap from '../GoogleMap/GoogleMap'
import FullMap from '../FullMap/FullMap'
import BrandingWidget from '../../../branding/BrandingWidget'
import BusinessCodeBanner from '../../../BusinessCodeBanner'
import showMobileMap from '../../actions/showMobileMap'
import setValue from '../../../shared/actions/setValue'
import {offersMockedData} from '@helpers/drupalSettings'
import OfferDurationBanner from '../OfferDurationBanner'
import FilterDetailedButton from '../FilterDetailed/FilterDetailedButton';
import fetchSearchResults from '../../actions/fetchSearchResults'
import ZoomButton from '../GoogleMap/ZoomButtonRedesign'
import ZoomButtonOut from '../GoogleMap/ZoomButtonOutRedesign';
import openPopup from '../../../PopupConstructor/actions/popupActions'
import { GLOBAL_ERROR_POPUP } from '../../../PopupConstructor/constants'
import {prepareDates} from 'js/scenes/MainSearchBox/helpers';
import {pushOpenDatesFilter} from 'js/scenes/MainSearchBox/customEvents';
import {useBrowser} from 'js/hooks/useBrowser';
import {sanitizeContent} from 'js/shared/security';

import styles from './SearchResults.module.scss'

const cx = classnames.bind(styles)
const searchQueryText = queryParameters?.q || ''
const reinsuranceBanner = window.drupalSettings?.reinsuranceBanner
const becomeMemberBanner = window.drupalSettings?.becomeMemberBanner
const maxNightsErrorPopin = window.drupalSettings?.searchResults?.maxNightsErrorPopin
const businessCode = Array.isArray(window?.drupalSettings?.code) ? null : window?.drupalSettings?.code;
const mapWrap = document.getElementById('map-holder');

const SearchResults = ({
  onShowMobileMap,
  setOwnValue,
  openPopupAction,
  brand_statement = [],
  results = [],
  switchMapLinkText = { map: 'Map', list: 'List view' },
  isOffers,
  filterDates = [],
  isFullMap = false,
  decreasingBanner,
  queryString,
  resultsCountWordings,
  fetchSearchResultsAction,
}) => {
  const browser = useBrowser();
  const prevQueryString = useRef(queryString);

  const onSearchDatesShow = () => {
    setTimeout(() => {
      pushOpenDatesFilter();
    }, 500)
  }

  const errorHandle = (msg) => {
    openPopupAction(GLOBAL_ERROR_POPUP, {
      message: msg,
      onCloseFunc: function (close) {
        pushOpenDatesFilter()
        close();
      }
    })
  }

  const onPageInitialised = () => {
    document.body.classList.add('page-initialised');
  }

  useEffect(() => {
    if (maxNightsErrorPopin?.message) {
      errorHandle(maxNightsErrorPopin?.message);
    }
    onPageInitialised();
    if (!filterDates?.length && isDesktop(browser)) {
      onSearchDatesShow();
    }
  }, []);

  useEffect(() => {
    const oldQueryString = prevQueryString.current;
    const newQueryString = queryString;

    if (typeof oldQueryString === 'string' && oldQueryString !== newQueryString) {
      urlParamUpdated({
        newQuery: newQueryString,
        oldQuery: oldQueryString,
        keys: ['dateIn', 'nbNight', 'apartment[]', 'sort', 'hotel_type', 'amenities', 'price_range_dynamic'],
      }, () => {
        if (!oldQueryString) {
          return;
        }
        fetchSearchResultsAction(newQueryString).then((data) => {
          if (data?.maxNightsErrorPopin?.message) {
            errorHandle(data?.maxNightsErrorPopin?.message)
          }
        })
      })
    }
    prevQueryString.current = newQueryString;
  }, [queryString, fetchSearchResultsAction]);

  const handleSwitchToMapView = () => {
    onShowMobileMap(true)
    setOwnValue('fullMap', true)
  }

  const isValidOfferPeriod = () => {
    if (!filterDates || !filterDates.length || !isOffers) return false

    const { from, to } = offersMockedData

    let fixedFilterDays = [...filterDates]

    if (filterDates.length === 2) {
      fixedFilterDays = [
        moment(filterDates[0]).add(1, 'seconds'),
        moment(filterDates[1]).subtract(1, 'seconds'),
      ]
    }

    return !!fixedFilterDays.find(date => !moment(date).isBetween(moment(from), moment(to).add(1, 'days'), null, '[]'))
  }

  const makePrediction = async (predictedUrl) => {
    return await fetchSearchResultsAction(predictedUrl, true)
  }

  const {
    map: mapText,
    list: listText,
  } = switchMapLinkText;

  const {
    hotelsInCity,
    hotelsInCityMobile
  } = resultsCountWordings || {};

  const resultsCountInfo = replaceString(
    isDesktop(browser) ? hotelsInCity : hotelsInCityMobile,
    {
      n: `<b>${results.length}</b>`,
      s: `<b>${searchQueryText}</b>`,
    }
  );
  const resultsForMap = results.map(({ id, price, coordinates, active, title }) => ({ id, price, coordinates, active, title }));

  return (
    <>
      <div className={cx('search-content', {isOffers})}>
        <div className={cx('results-navbar')}>
          <FilterDetailedButton
            makePrediction={makePrediction}
            resultsCount={results.length}
          />

          <p className={cx('results-info')} dangerouslySetInnerHTML={{__html: sanitizeContent(resultsCountInfo)}}/>

          {results.length > 0 && (
            <Button
              className={cx('map-link')}
              color="black"
              theme="rounded"
              size="sm"
              onClick={handleSwitchToMapView}
            >
              <i className={cx('icon-mappin')}/>
              {mapText}
            </Button>
          )}
        </div>

        <div className={cx('results-list')}>
          <div className={cx('brand-block')}>
            {(
              brand_statement
              && !(isValidOfferPeriod() && !!offersMockedData.durationBanner)
            ) && brand_statement.map(item => (
              <BrandingWidget
                key={item.href}
                image={item.image_link}
                content={item.text}
                href={item.href}
                hotelsCount={item.hotels_count}
                isOffers={isOffers}
              />
            ))}

            {isValidOfferPeriod() && !!offersMockedData.durationBanner && (
              <OfferDurationBanner
                isOffers={isOffers}
                text={offersMockedData.durationBanner}
              />
            )}

            {businessCode && (<BusinessCodeBanner data={businessCode}/>)}
          </div>

          <SearchList
            className={cx('search-list')}
            reinsuranceBanner={reinsuranceBanner}
            becomeMemberBanner={becomeMemberBanner}
            decreasingBanner={decreasingBanner}
            itemComponent={HotelListCard}
          />
        </div>
      </div>
      {results.length > 0 && isDesktop(browser) && (
        createPortal(
          <GoogleMap
            results={resultsForMap}
            hidden={isFullMap}
            isOffers={isOffers}
            zoomButton={ZoomButton}
            zoomOutButton={ZoomButtonOut}
            className={cx('map')}
          />,
          mapWrap
        )
      )}
      <FullMap
        isOffers={isOffers}
        hidden={!isFullMap}
        results={resultsForMap}
        zoomButton={ZoomButton}
        zoomOutButton={ZoomButtonOut}
        itemComponent={HotelListCardMini}
      />
    </>
  )
}

SearchResults.propTypes = {
  onShowMobileMap: PropTypes.func.isRequired,
  setOwnValue: PropTypes.func.isRequired,
  openPopupAction: PropTypes.func.isRequired,
  brand_statement: PropTypes.arrayOf(Object),
  results: PropTypes.arrayOf(Object),
  switchMapLinkText: PropTypes.shape({
    map: PropTypes.string,
    list: PropTypes.string,
  }),
  isOffers: PropTypes.bool.isRequired,
  filterDates: PropTypes.arrayOf(Object),
  isFullMap: PropTypes.bool,
}

export default connect(
  ({main, url}) => ({
    brand_statement: main.brand_statement,
    results: main.results,
    decreasingBanner: main.decreasingBanner,
    queryString: url.queryString,
    switchMapLinkText: main.switchMapLinkText,
    isFullMap: main.fullMap,
    filterDates: prepareDates({
      dateIn: url?.parameters?.dateIn,
      nbNight: url?.parameters?.nbNight,
    }),
    resultsCountWordings: main?.setting?.widgets?.filters?.sort,
    isOffers: offersMockedData && Object.keys(offersMockedData).length > 0 && offersMockedData.list?.length > 0,
  }),
  dispatch => ({
    onShowMobileMap: isMobile => dispatch(showMobileMap(isMobile)),
    setOwnValue: (key, value) => dispatch(setValue(key, value)),
    fetchSearchResultsAction: (...params) => dispatch(fetchSearchResults(...params)),
    openPopupAction: (...params) => dispatch(openPopup(...params)),
  }),
)(SearchResults)
